import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { BrutalBadge } from "@/components/ui/BrutalBadge";
import { Checkbox } from "@/components/ui/checkbox";
import { inclusivityTags, inclusiveBenefits, InclusivityTag } from "@/data/jobOptions";
import { JobFormData } from "@/pages/dashboard/JobCreate";
import { Heart, Plus, X } from "lucide-react";
import { sanitizeText } from "@/lib/sanitize";

interface JobFormStep3Props {
  formData: JobFormData;
  updateFormData: (updates: Partial<JobFormData>) => void;
}

export function JobFormStep3({ formData, updateFormData }: JobFormStep3Props) {
  const { t } = useTranslation();
  const [customBenefit, setCustomBenefit] = useState('');

  const toggleInclusivityTag = (tag: InclusivityTag) => {
    const current = formData.inclusivity_tags;
    if (current.includes(tag)) {
      updateFormData({ inclusivity_tags: current.filter(t => t !== tag) });
    } else {
      updateFormData({ inclusivity_tags: [...current, tag] });
    }
  };

  const toggleBenefit = (benefit: string) => {
    const current = formData.inclusive_benefits;
    if (current.includes(benefit)) {
      updateFormData({ inclusive_benefits: current.filter(b => b !== benefit) });
    } else {
      updateFormData({ inclusive_benefits: [...current, benefit] });
    }
  };

  const addCustomBenefit = () => {
    const cleanedBenefit = sanitizeText(customBenefit.trim());
    if (cleanedBenefit && !formData.inclusive_benefits.includes(cleanedBenefit)) {
      updateFormData({ inclusive_benefits: [...formData.inclusive_benefits, cleanedBenefit] });
      setCustomBenefit('');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-xl font-bold text-dark mb-2">
          {t("jobs.form.step3Title")}
        </h2>
        <p className="text-muted-foreground">
          {t("jobs.form.step3Desc")}
        </p>
      </div>

      {/* Inclusivity Tags */}
      <div>
        <label className="block font-semibold text-dark mb-3">
          {t("jobs.form.inclusivityTags")} *
        </label>
        <p className="text-sm text-muted-foreground mb-4">
          {t("jobs.form.inclusivityTagsDesc")}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {inclusivityTags.map((tag) => {
            const isSelected = formData.inclusivity_tags.includes(tag.value);
            return (
              <button
                key={tag.value}
                type="button"
                onClick={() => toggleInclusivityTag(tag.value)}
                className={`p-4 border-[3px] border-dark text-left transition-all ${
                  isSelected 
                    ? 'bg-primary shadow-[4px_4px_0px_#101322] -translate-x-1 -translate-y-1' 
                    : 'bg-card hover:bg-surface'
                }`}
              >
                <div className="text-2xl mb-1">{tag.icon}</div>
                <div className="font-semibold text-dark text-sm">{t(tag.labelKey)}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Inclusive Benefits */}
      <div>
        <label className="block font-semibold text-dark mb-3 flex items-center gap-2">
          <Heart className="w-5 h-5 text-primary" />
          {t("jobs.form.inclusiveBenefits")}
        </label>
        <p className="text-sm text-muted-foreground mb-4">
          {t("jobs.form.inclusiveBenefitsDesc")}
        </p>
        
        <div className="space-y-3 mb-4">
          {inclusiveBenefits.map((benefit) => {
            const isSelected = formData.inclusive_benefits.includes(benefit.value);
            return (
              <label
                key={benefit.value}
                className={`flex items-center gap-3 p-3 border-[2px] border-dark cursor-pointer transition-colors ${
                  isSelected ? 'bg-primary/20' : 'bg-card hover:bg-surface'
                }`}
              >
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => toggleBenefit(benefit.value)}
                />
                <span className="text-lg">{benefit.icon}</span>
                <span className="font-medium text-dark">{t(benefit.labelKey)}</span>
              </label>
            );
          })}
        </div>

        {/* Custom benefit */}
        <div className="flex gap-2">
          <BrutalInput
            value={customBenefit}
            onChange={(e) => setCustomBenefit(e.target.value)}
            placeholder={t("jobs.form.addCustomBenefit")}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomBenefit())}
          />
          <button
            type="button"
            onClick={addCustomBenefit}
            className="px-4 py-2 bg-primary border-[3px] border-dark hover:bg-primary/80 transition-colors"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>

        {/* Custom benefits added */}
        {formData.inclusive_benefits.filter(b => !inclusiveBenefits.find(ib => ib.value === b)).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {formData.inclusive_benefits
              .filter(b => !inclusiveBenefits.find(ib => ib.value === b))
              .map((benefit) => (
                <BrutalBadge key={benefit} variant="default" className="pr-1">
                  {benefit}
                  <button
                    type="button"
                    onClick={() => toggleBenefit(benefit)}
                    className="ml-2 hover:text-destructive"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </BrutalBadge>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
